/**
 * Drizzle query builders + query keys for SCHEDULE and ATTENDANCE (F3-A).
 *
 * Local-first consumption over the per-role streams (my_cohorts_student /
 * my_cohorts_teacher / campus_admin_scope / org_admin_scope): schedule slots,
 * class sessions, attendance and risk alerts. Writes go through the
 * write-path (mutations.ts) or REST (/api/attendance, /api/schedule,
 * /api/cohorts/:id/generate-sessions).
 */

import {
  and,
  asc,
  attendance_record,
  class_session,
  cohort_enrollment,
  eq,
  gte,
  isNull,
  lte,
  type PowerSyncDrizzleDB,
  schedule_slot,
  student_alert,
} from "@space/powersync";

export const scheduleQueryKeys = {
  slotsByCohort: (cohortId: string | undefined) => ["academy-schedule", "slots", cohortId] as const,
  sessionsByCohort: (cohortId: string | undefined) =>
    ["academy-schedule", "sessions", cohortId] as const,
  sessionsBetween: (userId: string | undefined, from: string, to: string) =>
    ["academy-schedule", "my-sessions", userId, from, to] as const,
  attendanceBySession: (sessionId: string | undefined) =>
    ["academy-attendance", "session", sessionId] as const,
  attendanceByCohort: (cohortId: string | undefined) =>
    ["academy-attendance", "cohort", cohortId] as const,
  myAttendance: (userId: string | undefined) => ["academy-attendance", "mine", userId] as const,
  alertsByCohort: (cohortId: string | undefined) => ["academy-alerts", "cohort", cohortId] as const,
  // F3-G: a session by id (live class screen).
  sessionById: (sessionId: string | undefined) =>
    ["academy-schedule", "session", sessionId] as const,
};

// ─── Schedule ─────────────────────────────────────────────────────────────────

/** A cohort's weekly schedule slots (ordered by day and time). */
export function getSlotsByCohortQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(schedule_slot)
    .where(and(eq(schedule_slot.cohort_id, cohortId), isNull(schedule_slot.deleted_at)))
    .orderBy(asc(schedule_slot.weekday), asc(schedule_slot.start_time));
}

/** A class session by id (F3-G, live class screen). */
export function getSessionByIdQuery(db: PowerSyncDrizzleDB, sessionId: string) {
  return db
    .select()
    .from(class_session)
    .where(and(eq(class_session.id, sessionId), isNull(class_session.deleted_at)))
    .limit(1);
}

/** A cohort's class sessions (ascending by date). */
export function getSessionsByCohortQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(class_session)
    .where(and(eq(class_session.cohort_id, cohortId), isNull(class_session.deleted_at)))
    .orderBy(asc(class_session.date), asc(class_session.start_time));
}

/**
 * MY sessions between two dates (for the personal timetable). Crosses the
 * cohorts where I'm ENROLLED (my_cohorts_student synced its class_session);
 * the teacher schedule arrives via my_cohorts_teacher (same class_session rows).
 */
export function getMySessionsBetweenQuery(db: PowerSyncDrizzleDB, from: string, to: string) {
  return db
    .select()
    .from(class_session)
    .where(
      and(
        isNull(class_session.deleted_at),
        gte(class_session.date, from),
        lte(class_session.date, to),
      ),
    )
    .orderBy(asc(class_session.date), asc(class_session.start_time));
}

// ─── Attendance ──────────────────────────────────────────────────────────────

/** Attendance of a session (one row per student). */
export function getAttendanceBySessionQuery(db: PowerSyncDrizzleDB, sessionId: string) {
  return db
    .select()
    .from(attendance_record)
    .where(eq(attendance_record.class_session_id, sessionId));
}

/** MY attendance (own rows, my_cohorts_student stream). */
export function getMyAttendanceQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(attendance_record).where(eq(attendance_record.user_id, userId));
}

/**
 * Per-student attendance summary of a cohort (derived stats).
 *
 * **D3 (plan 06 · E3):** no `leftJoin(user, …)`. With no identity in the
 * query there also can't be an `ORDER BY user.name`: ordering by name is
 * applied by `sortByIdentityName` after hydrating, and here it's ordered by
 * enrollment so the table comes out stable while identity is in transit.
 */
export function getCohortAttendanceReportQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select({
      user_id: cohort_enrollment.user_id,
      status: cohort_enrollment.status,
      present: cohort_enrollment.sessions_present,
      late: cohort_enrollment.sessions_late,
      absent: cohort_enrollment.sessions_absent,
      excused: cohort_enrollment.sessions_excused,
      absence_streak: cohort_enrollment.absence_streak,
      attendance_rate: cohort_enrollment.attendance_rate,
    })
    .from(cohort_enrollment)
    .where(and(eq(cohort_enrollment.cohort_id, cohortId), isNull(cohort_enrollment.deleted_at)))
    .orderBy(asc(cohort_enrollment.created_at));
}

/** Attendance report row WITHOUT identity. */
export interface AttendanceReportRow {
  user_id: string | null;
  status: string | null;
  present: number | null;
  late: number | null;
  absent: number | null;
  excused: number | null;
  absence_streak: number | null;
  attendance_rate: number | null;
}

// ─── Alerts ─────────────────────────────────────────────────────────────────

/**
 * OPEN risk alerts of a cohort (staff).
 *
 * **D3 (plan 06 · E3):** no `leftJoin(user, …)`. The alert's student name is
 * supplied by the single identity source and gets attached with `attachIdentity`.
 */
export function getOpenAlertsByCohortQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select({
      id: student_alert.id,
      user_id: student_alert.user_id,
      kind: student_alert.kind,
      payload: student_alert.payload,
      status: student_alert.status,
      created_at: student_alert.created_at,
    })
    .from(student_alert)
    .where(and(eq(student_alert.cohort_id, cohortId), eq(student_alert.status, "open")))
    .orderBy(asc(student_alert.created_at));
}

/** Alert row WITHOUT identity. */
export interface AlertRow {
  id: string;
  user_id: string | null;
  kind: string | null;
  payload: string | null;
  status: string | null;
  created_at: string | null;
}
